import { GetStaticProps, NextPage } from 'next';
import { groq } from 'next-sanity';
import React from 'react';
import GrayLink from '../../../components/GrayLink';
import ArticlesByCategory from '../../../components/learn/articles/ArticlesByCategory';
import { getClient } from '../../../lib/sanity.server';
import { Article } from './[slug]';

interface Props {
  articles: Array<Article>;
  categories: Array<Category>;
}

export interface Category {
  title: string;
  slug: string;
  description: string;
}

const ArticlesPage: NextPage<Props> = ({ articles, categories }) => {
  return (
    <div className='max-w-6xl mx-auto mb-32'>
      <div className='mt-32'>
        <GrayLink link='/learn' text='Learn' />
      </div>
      <h1 className='mt-2'>Online Ministry Resources</h1>
      {categories.map(category => {
        const connectedArticles = articles.filter(article => article.category === category.slug);
        return (
          <ArticlesByCategory
            key={category.slug}
            category={category}
            articles={connectedArticles}
            limitArticles={true}
            isLandingPage={true}
          />
        );
      })}
    </div>
  );
};

export const getStaticProps: GetStaticProps<{
  articles: Array<Article>;
  categories: Array<Category>;
}> = async () => {
  const articles = await getClient().fetch<Array<Article>>(
    groq`
    *[_type=="article"] {
      "slug": slug.current,
      "coverVideoUrl": media[0]->.assetId,
      "coverImageAsset": media[0].asset,
      title,
      "category": category->.slug.current
    }
  `
  );

  const categories = await getClient().fetch<Array<Category>>(
    groq`
    *[_type=="articleCategory"] {
      title,
      description,
      "slug": slug.current
    }
  `
  );

  const articlesWithVideos = await Promise.all(
    articles.map(async article => {
      if (article.coverVideoUrl) {
        const response = await fetch(
          `http://fast.wistia.net/oembed?url=http://home.wistia.com/medias/${article.coverVideoUrl}?embedType=async&videoWidth=640`,
          {
            method: 'GET',
          }
        );

        const data = await response.json();
        article.coverVideoUrl = data.thumbnail_url;
      }
      return article;
    })
  );

  return {
    props: {
      articles: articlesWithVideos,
      categories,
    },
    revalidate: process.env.APP_ENV === 'production' ? 60000 : 1,
  };
};

export default ArticlesPage;
